/**
 * Modal body: pick questionnaires and generate a shareable short survey link.
 * @agentic connections
 */

import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { showErrorNotification } from "@benchmark/ui/utils";
import { IconAlertCircle, IconCopy, IconLink } from "@tabler/icons-react";
import { useMedplum } from "@medplum/react";
import {
  isPatientUnder18ForNps,
  PATIENT_UNDER_18_FLAG_CODE,
} from "@benchmark/core/utils";
import type { Flag, Patient } from "@medplum/fhirtypes";
import { useCallback, useEffect, useState } from "react";

import { requestNpsSurveyLink } from "@/utils/api/requestNpsSurveyLink";
import { requestScreenerLink } from "@/utils/api/requestScreenerLink";
import { requestSurveyLink } from "@/utils/api/requestSurveyLink";
import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";

import { SURVEY_LINK_QUESTIONNAIRE_OPTIONS } from "./surveyLinkQuestionnaireOptions";

/**
 * Props for SurveyLinkModalContent.
 * @agentic connections
 */
type SurveyLinkModalContentProps = {
  patientId: string;
  onSuccess?: () => void;
  onCancel: () => void;
};

/**
 * Patient survey link generator UI for the modal provider.
 * @agentic connections
 */
export const SurveyLinkModalContent = ({
  patientId,
  onSuccess,
  onCancel,
}: SurveyLinkModalContentProps) => {
  const medplum = useMedplum();

  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [npsUrl, setNpsUrl] = useState<string | null>(null);
  const [npsExpiresAt, setNpsExpiresAt] = useState<string | null>(null);
  const [isGeneratingNps, setIsGeneratingNps] = useState(false);
  const [screenerUrl, setScreenerUrl] = useState<string | null>(null);
  const [isGeneratingScreener, setIsGeneratingScreener] = useState(false);
  const [npsBlockedForMinor, setNpsBlockedForMinor] = useState(false);
  const [isLoadingNpsEligibility, setIsLoadingNpsEligibility] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadNpsEligibility = async () => {
      setIsLoadingNpsEligibility(true);
      try {
        const patient = await medplum.readResource("Patient", patientId);
        const flags = await medplum.searchResources("Flag", {
          subject: `Patient/${patientId}`,
          status: "active",
        });
        const hasMinorFlag = flags.some((flag: Flag) =>
          flag.code?.coding?.some(
            (coding) => coding.code === PATIENT_UNDER_18_FLAG_CODE,
          ),
        );
        if (!cancelled) {
          setNpsBlockedForMinor(
            isPatientUnder18ForNps(patient as Patient, hasMinorFlag),
          );
        }
      } catch {
        if (!cancelled) {
          setNpsBlockedForMinor(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingNpsEligibility(false);
        }
      }
    };

    void loadNpsEligibility();

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId]);

  const handleGenerate = useCallback(async () => {
    if (selectedUrls.length === 0) {
      setError("Select at least one survey.");
      return;
    }

    setError(null);
    setIsGenerating(true);
    setGeneratedUrl(null);

    try {
      const accessToken = await ensureActiveSession(medplum);
      const result = await requestSurveyLink({
        accessToken,
        patientId,
        questionnaireUrls: selectedUrls,
      });
      setGeneratedUrl(result.url);
      onSuccess?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not generate survey link.";
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  }, [medplum, onSuccess, patientId, selectedUrls]);

  const handleCopy = useCallback(async () => {
    if (!generatedUrl || !navigator?.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedUrl);
      notifications.show({
        title: "Copied",
        message: "Survey link copied to clipboard.",
        color: "green",
      });
    } catch {
      showErrorNotification("Clipboard is not available.", {
        title: "Copy failed",
      });
    }
  }, [generatedUrl]);

  const handleGenerateNps = useCallback(async () => {
    setError(null);
    setIsGeneratingNps(true);
    setNpsUrl(null);
    setNpsExpiresAt(null);

    try {
      const accessToken = await ensureActiveSession(medplum);
      const result = await requestNpsSurveyLink({
        accessToken,
        patientId,
      });
      setNpsUrl(result.url);
      setNpsExpiresAt(result.expiresAt);
      onSuccess?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not generate NPS link.";
      setError(message);
    } finally {
      setIsGeneratingNps(false);
    }
  }, [medplum, onSuccess, patientId]);

  const handleCopyNps = useCallback(async () => {
    if (!npsUrl || !navigator?.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(npsUrl);
      notifications.show({
        title: "Copied",
        message: "NPS link copied to clipboard.",
        color: "green",
      });
    } catch {
      showErrorNotification("Clipboard is not available.", {
        title: "Copy failed",
      });
    }
  }, [npsUrl]);

  const handleGenerateScreener = useCallback(async () => {
    setError(null);
    setIsGeneratingScreener(true);
    setScreenerUrl(null);

    try {
      const accessToken = await ensureActiveSession(medplum);
      const result = await requestScreenerLink({
        accessToken,
        patientId,
      });
      setScreenerUrl(result.url);
      onSuccess?.();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Could not generate screener link.";
      setError(message);
    } finally {
      setIsGeneratingScreener(false);
    }
  }, [medplum, onSuccess, patientId]);

  const handleCopyScreener = useCallback(async () => {
    if (!screenerUrl || !navigator?.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(screenerUrl);
      notifications.show({
        title: "Copied",
        message: "Screener link copied to clipboard.",
        color: "green",
      });
    } catch {
      showErrorNotification("Clipboard is not available.", {
        title: "Copy failed",
      });
    }
  }, [screenerUrl]);

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Member feedback (NPS)
        </Text>
        <Text size="sm" c="dimmed">
          Anonymous 0–10 survey. No birthday verification. Link is valid for 14
          days. Not available for patients under 18.
        </Text>
        {npsBlockedForMinor && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="orange"
            variant="light"
            title="NPS not available"
          >
            NPS surveys are not sent to patients under 18.
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            variant="light"
            loading={isGeneratingNps}
            disabled={npsBlockedForMinor || isLoadingNpsEligibility}
            onClick={() => {
              void handleGenerateNps();
            }}
          >
            Generate NPS link
          </Button>
        </Group>
        {npsUrl && (
          <Stack gap="xs">
            {npsExpiresAt && (
              <Text size="xs" c="dimmed">
                Expires {new Date(npsExpiresAt).toLocaleString()}
              </Text>
            )}
            <Group wrap="nowrap" gap="xs" align="flex-end">
              <TextInput
                style={{ flex: 1 }}
                label="NPS link"
                readOnly
                value={npsUrl}
              />
              <Button
                leftSection={<IconCopy size={16} />}
                onClick={() => {
                  void handleCopyNps();
                }}
              >
                Copy
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>

      <Divider label="Adaptive mental-health screener" labelPosition="center" />

      <Stack gap="xs">
        <Text size="sm" c="dimmed">
          A single DOB-gated link that adapts questions as the member answers,
          saves progress across devices, and resolves first check-in vs
          follow-up automatically. Link is valid for 14 days.
        </Text>
        <Group justify="flex-end">
          <Button
            variant="light"
            loading={isGeneratingScreener}
            onClick={() => {
              void handleGenerateScreener();
            }}
          >
            Generate screener link
          </Button>
        </Group>
        {screenerUrl && (
          <Group wrap="nowrap" gap="xs" align="flex-end">
            <TextInput
              style={{ flex: 1 }}
              label="Screener link"
              readOnly
              value={screenerUrl}
            />
            <Button
              leftSection={<IconCopy size={16} />}
              onClick={() => {
                void handleCopyScreener();
              }}
            >
              Copy
            </Button>
          </Group>
        )}
      </Stack>

      <Divider label="Clinical assessments" labelPosition="center" />

      <Text size="sm" c="dimmed">
        Choose which assessments to include. The member will confirm their date
        of birth before starting. The link is shortened for texting.
      </Text>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Could not create link"
          color="red"
          variant="light"
        >
          {error}
        </Alert>
      )}

      <ScrollArea.Autosize mah={280} type="auto" offsetScrollbars>
        <Checkbox.Group
          value={selectedUrls}
          onChange={setSelectedUrls}
          label="Surveys"
        >
          <Stack gap="xs" mt="xs">
            {SURVEY_LINK_QUESTIONNAIRE_OPTIONS.map((opt) => (
              <Checkbox key={opt.url} value={opt.url} label={opt.label} />
            ))}
          </Stack>
        </Checkbox.Group>
      </ScrollArea.Autosize>

      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={onCancel}>
          Close
        </Button>
        <Button
          leftSection={<IconLink size={18} />}
          loading={isGenerating}
          disabled={selectedUrls.length === 0}
          onClick={() => {
            void handleGenerate();
          }}
        >
          Generate link
        </Button>
      </Group>

      {generatedUrl && (
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Share this link
          </Text>
          <Group wrap="nowrap" gap="xs" align="flex-end">
            <TextInput
              style={{ flex: 1 }}
              label="Link"
              readOnly
              value={generatedUrl}
            />
            <Button
              leftSection={<IconCopy size={16} />}
              onClick={() => {
                void handleCopy();
              }}
            >
              Copy
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
};
